/**
 * CategoryFilterSelect - Seletor de categoria hierárquico para filtros
 * 
 * Combina a interface de dropdown do CategoryTreeSelect com a árvore
 * hierárquica e busca do CategorySidebar para uso em filtros.
 * 
 * Características:
 * - Exibição em árvore com indentação visual
 * - Busca por nome com highlight
 * - Expansão/colapso de categorias pai
 * - Opção "Todas categorias" no topo
 * - Ícones de pasta coloridos por categoria
 * - Suporte a contagem por categoria (opcional)
 * 
 * @author Sistema
 * @version 1.0.0
 */

import * as React from 'react';
import { Check, ChevronsUpDown, ChevronRight, Folder, FolderOpen, LayoutGrid, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { useCrmCategories, CrmProductCategory } from '@/hooks/useCrmCategories';

// ============================================================
// INTERFACES E TIPOS
// ============================================================

/** Props do componente CategoryFilterSelect */
interface CategoryFilterSelectProps {
  /** ID da categoria selecionada (null = todas) */
  value: string | null;
  /** Callback ao selecionar categoria */
  onChange: (categoryId: string | null) => void;
  /** Placeholder do botão */
  placeholder?: string;
  /** Contagem de itens por categoria (opcional) */
  categoryCounts?: Record<string, number>;
  /** Contagem total para "Todas categorias" (opcional) */
  totalCount?: number;
  /** ID do workspace para filtrar categorias */
  workspaceId?: string | null;
  /** Desabilitar seletor */
  disabled?: boolean;
  /** Classes CSS adicionais */
  className?: string;
  /** Tamanho do botão */
  size?: 'xs' | 'sm' | 'default';
  /**
   * Quando true, categorias que possuem filhas NÃO podem ser selecionadas
   * (apenas folhas são selecionáveis). Usado no cadastro/edição de produtos
   * para forçar que cada produto seja vinculado a uma categoria folha.
   * O clique em categoria-pai apenas expande/colapsa a árvore.
   */
  leafOnly?: boolean;
}

/** Props do item de categoria na árvore */
interface CategoryTreeItemProps {
  category: CrmProductCategory;
  level: number;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  counts?: Record<string, number>;
  expandedIds: Set<string>;
  onToggleExpand: (id: string) => void;
  searchQuery: string;
  /** Se true, categorias com filhas não são selecionáveis (só folhas) */
  leafOnly?: boolean;
}

// ============================================================
// FUNÇÕES UTILITÁRIAS
// ============================================================

/**
 * Renderiza texto com highlight na parte correspondente à busca
 */
function HighlightText({ text, query }: { text: string; query: string }) {
  if (!query.trim()) return <>{text}</>;
  
  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const index = lowerText.indexOf(lowerQuery);
  
  if (index === -1) return <>{text}</>;
  
  const before = text.slice(0, index);
  const match = text.slice(index, index + query.length);
  const after = text.slice(index + query.length);
  
  return (
    <>
      {before}
      <mark className="bg-yellow-200 dark:bg-yellow-800 rounded px-0.5">{match}</mark>
      {after}
    </>
  );
}

/**
 * Filtra categorias recursivamente mantendo hierarquia
 */
function filterCategories(
  categories: CrmProductCategory[],
  query: string
): CrmProductCategory[] {
  if (!query.trim()) return categories;
  
  const lowerQuery = query.toLowerCase();
  
  return categories
    .map(category => {
      const nameMatches = category.name.toLowerCase().includes(lowerQuery);
      const filteredChildren = category.children 
        ? filterCategories(category.children, query)
        : [];
      
      if (nameMatches || filteredChildren.length > 0) {
        return {
          ...category,
          children: filteredChildren.length > 0 ? filteredChildren : category.children
        };
      }
      return null;
    })
    .filter(Boolean) as CrmProductCategory[];
}

/**
 * Coleta IDs de categorias pai que contêm resultados de busca
 */
function collectMatchingParentIds(
  categories: CrmProductCategory[],
  query: string
): Set<string> {
  const ids = new Set<string>();
  if (!query.trim()) return ids;
  
  const lowerQuery = query.toLowerCase();
  
  const collect = (cats: CrmProductCategory[], parentIds: string[]) => {
    cats.forEach(cat => {
      const currentPath = [...parentIds, cat.id];
      const nameMatches = cat.name.toLowerCase().includes(lowerQuery);
      
      if (nameMatches) {
        parentIds.forEach(id => ids.add(id));
      }
      
      if (cat.children) {
        collect(cat.children, currentPath);
      }
    });
  };
  
  collect(categories, []);
  return ids;
}

/**
 * Coleta todos os IDs de categorias recursivamente
 */
function collectAllCategoryIds(categories: CrmProductCategory[]): Set<string> {
  const ids = new Set<string>();
  const collect = (cats: CrmProductCategory[]) => {
    cats.forEach(cat => {
      ids.add(cat.id);
      if (cat.children) collect(cat.children);
    });
  };
  collect(categories);
  return ids;
}

/**
 * Encontra categoria por ID na árvore
 */
function findCategoryById(
  categories: CrmProductCategory[],
  id: string
): CrmProductCategory | null {
  for (const cat of categories) {
    if (cat.id === id) return cat;
    if (cat.children) {
      const found = findCategoryById(cat.children, id);
      if (found) return found;
    }
  }
  return null;
}

// ============================================================
// COMPONENTES
// ============================================================

/**
 * Item individual de categoria na árvore
 */
function CategoryTreeItem({
  category,
  level,
  selectedId,
  onSelect,
  counts,
  expandedIds,
  onToggleExpand,
  searchQuery,
  leafOnly = false,
}: CategoryTreeItemProps) {
  const hasChildren = category.children && category.children.length > 0;
  const isExpanded = expandedIds.has(category.id);
  const isSelected = selectedId === category.id;
  const count = counts?.[category.id];

  // Regra de negócio: quando leafOnly está ativo (cadastro/edição de produto),
  // categorias-pai (que têm filhas) NÃO podem ser selecionadas. O clique no row
  // apenas alterna a expansão da árvore, forçando o usuário a escolher uma folha.
  const isParentBlocked = leafOnly && hasChildren;

  const handleRowActivate = () => {
    if (isParentBlocked) {
      // Pai bloqueado: alterna expansão em vez de selecionar
      onToggleExpand(category.id);
      return;
    }
    onSelect(category.id);
  };

  return (
    <div className="w-full">
      {/* Item principal */}
      <div
        role="button"
        tabIndex={0}
        onClick={handleRowActivate}
        onKeyDown={(e) => e.key === 'Enter' && handleRowActivate()}
        title={isParentBlocked ? 'Selecione uma subcategoria' : undefined}
        aria-disabled={isParentBlocked || undefined}
        className={cn(
          'w-full flex items-center gap-1.5 px-2 py-1.5 text-xs rounded-md transition-colors',
          isParentBlocked
            ? 'cursor-default hover:bg-muted/40 opacity-95'
            : 'cursor-pointer hover:bg-accent/50',
          isSelected && !isParentBlocked && 'bg-accent text-accent-foreground font-medium'
        )}
        style={{ paddingLeft: `${8 + level * 12}px` }}
      >
        {/* Ícone de expansão */}
        {hasChildren ? (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleExpand(category.id);
            }}
            className="p-0.5 hover:bg-muted rounded shrink-0"
          >
            <ChevronRight
              className={cn(
                'h-3 w-3 transition-transform duration-200',
                isExpanded && 'rotate-90'
              )}
            />
          </button>
        ) : (
          <span className="w-4 shrink-0" />
        )}

        {/* Ícone de pasta com cor */}
        {isExpanded && hasChildren ? (
          <FolderOpen
            className="h-3.5 w-3.5 shrink-0"
            style={{ color: category.color || 'currentColor' }}
          />
        ) : (
          <Folder
            className="h-3.5 w-3.5 shrink-0"
            style={{ color: category.color || 'currentColor' }}
          />
        )}

        {/* Nome da categoria com highlight */}
        <span
          className={cn(
            'flex-1 text-left truncate',
            isParentBlocked && 'text-muted-foreground'
          )}
        >
          <HighlightText text={category.name} query={searchQuery} />
        </span>

        {/* Check de seleção (não exibe em pais bloqueados) */}
        {isSelected && !isParentBlocked && (
          <Check className="h-3.5 w-3.5 text-primary shrink-0" />
        )}

        {/* Contador */}
        {count !== undefined && count > 0 && (
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 shrink-0 ml-1">
            {count}
          </Badge>
        )}
      </div>

      {/* Filhos (se expandido) */}
      {hasChildren && isExpanded && (
        <div>
          {category.children!.map((child) => (
            <CategoryTreeItem
              key={child.id}
              category={child}
              level={level + 1}
              selectedId={selectedId}
              onSelect={onSelect}
              counts={counts}
              expandedIds={expandedIds}
              onToggleExpand={onToggleExpand}
              searchQuery={searchQuery}
              leafOnly={leafOnly}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// COMPONENTE PRINCIPAL
// ============================================================

/**
 * Componente seletor de categoria hierárquico para filtros
 */
export function CategoryFilterSelect({
  value,
  onChange,
  placeholder = 'Todas categorias',
  categoryCounts,
  totalCount,
  workspaceId,
  disabled = false,
  className,
  size = 'sm',
  leafOnly = false,
}: CategoryFilterSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [expandedIds, setExpandedIds] = React.useState<Set<string>>(new Set());
  const hasInitiallyExpanded = React.useRef(false);
  // Ref para o container rolável da lista — usado para fazer scroll manual via wheel
  // (necessário porque Radix Dialog + react-remove-scroll bloqueia eventos wheel
  // do touchpad em popovers portalados para fora do DialogContent)
  const listRef = React.useRef<HTMLDivElement>(null);

  // Buscar categorias (filtradas por workspace se fornecido)
  const { categoryTree, categories, isLoading } = useCrmCategories({ workspaceId });

  // Expandir todas as categorias na primeira vez que a árvore carregar
  React.useEffect(() => {
    if (categoryTree.length > 0 && !hasInitiallyExpanded.current) {
      setExpandedIds(collectAllCategoryIds(categoryTree));
      hasInitiallyExpanded.current = true;
    }
  }, [categoryTree]);

  // Expandir automaticamente pais com resultados ao buscar
  React.useEffect(() => {
    if (searchQuery.trim() && categoryTree.length > 0) {
      const matchingParents = collectMatchingParentIds(categoryTree, searchQuery);
      setExpandedIds(prev => new Set([...prev, ...matchingParents]));
    }
  }, [searchQuery, categoryTree]);

  // Categorias filtradas pela busca
  const filteredCategories = React.useMemo(
    () => filterCategories(categoryTree, searchQuery),
    [categoryTree, searchQuery]
  );

  // Encontra categoria selecionada
  const selectedCategory = value ? findCategoryById(categoryTree, value) : null;

  // Toggle expansão de categoria
  const handleToggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Selecionar categoria
  const handleSelect = (id: string | null) => {
    onChange(id);
    setOpen(false);
    setSearchQuery('');
  };

  // Limpar busca ao fechar
  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setSearchQuery('');
    }
  };

  // Estilos de tamanho
  const sizeStyles = {
    xs: 'h-7 px-2.5 text-xs',
    sm: 'h-8 px-3 text-xs',
    default: 'h-9 px-4 text-sm',
  };

  /**
   * Trata wheel do touchpad/mouse na lista de categorias.
   *
   * Quando o Popover é aberto dentro de um Dialog (Radix), o react-remove-scroll
   * usado pelo Dialog cancela eventos wheel que "vazam" para o body. Como o
   * PopoverContent é portalado, o trackpad acaba sendo bloqueado.
   *
   * Solução: capturamos o wheel ANTES dele subir, paramos a propagação para que
   * o react-remove-scroll do Dialog não cancele, e — se ainda há espaço para
   * rolar internamente — fazemos scroll manual. Se já está no topo/fim,
   * deixamos o evento padrão seguir (sem travar).
   */
  const handleListWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    const el = listRef.current;
    if (!el) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    const goingDown = e.deltaY > 0;
    const atTop = scrollTop <= 0;
    const atBottom = scrollTop + clientHeight >= scrollHeight - 1;

    // Sempre impedir bubbling para o Dialog pai (evita cancelamento do wheel)
    e.stopPropagation();

    // Se ainda há espaço para rolar na direção desejada, faz scroll manual
    if ((goingDown && !atBottom) || (!goingDown && !atTop)) {
      el.scrollTop += e.deltaY;
      e.preventDefault();
    }
  };

  /** Trackpads que emitem touch events: apenas isolamos do Dialog pai */
  const handleListTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || isLoading}
          className={cn(
            'justify-between',
            sizeStyles[size],
            value && 'border-primary bg-primary/5',
            className
          )}
        >
          {selectedCategory ? (
            <div className="flex items-center gap-2 truncate">
              <Folder
                className="h-3.5 w-3.5 shrink-0"
                style={{ color: selectedCategory.color || 'currentColor' }}
              />
              <span className="truncate">{selectedCategory.name}</span>
            </div>
          ) : (
            <span className="text-muted-foreground truncate">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        {/* Campo de busca customizado */}
        <div className="flex items-center border-b px-3 py-2">
          <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
          <input
            placeholder="Buscar categoria..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex h-8 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="p-1 hover:bg-muted rounded"
            >
              <X className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          )}
        </div>

        {/* Lista de categorias */}
        <div
          ref={listRef}
          className="max-h-[300px] overflow-y-auto p-1"
          style={{ pointerEvents: 'auto', overscrollBehavior: 'contain' }}
          onWheelCapture={handleListWheel}
          onTouchMoveCapture={handleListTouchMove}
        >
          {/* Opção "Todas categorias" */}
          <div
            role="button"
            tabIndex={0}
            onClick={() => handleSelect(null)}
            onKeyDown={(e) => e.key === 'Enter' && handleSelect(null)}
            className={cn(
              'w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded-md transition-colors cursor-pointer',
              'hover:bg-accent/50',
              value === null && 'bg-accent text-accent-foreground font-medium'
            )}
          >
            <LayoutGrid className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="flex-1 text-left">{placeholder}</span>
            {value === null && (
              <Check className="h-3.5 w-3.5 text-primary" />
            )}
            {totalCount !== undefined && (
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
                {totalCount}
              </Badge>
            )}
          </div>

          {/* Separador */}
          <div className="my-1 h-px bg-border" />

          {/* Árvore de Categorias */}
          {isLoading ? (
            <div className="px-2 py-4 text-xs text-muted-foreground text-center">
              Carregando...
            </div>
          ) : filteredCategories.length === 0 ? (
            <div className="px-2 py-4 text-xs text-muted-foreground text-center">
              {searchQuery ? 'Nenhuma categoria encontrada' : 'Nenhuma categoria cadastrada'}
            </div>
          ) : (
            filteredCategories.map((category) => (
              <CategoryTreeItem
                key={category.id}
                category={category}
                level={0}
                selectedId={value}
                onSelect={handleSelect}
                counts={categoryCounts}
                expandedIds={expandedIds}
                onToggleExpand={handleToggleExpand}
                searchQuery={searchQuery}
                leafOnly={leafOnly}
              />
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
